import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import BookAssessment from './BookAssessment';

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

// The body copy is authored HTML, so prose supplies the list markers and rhythm.
// Everything it decides about voice is overridden back onto the page's own system.
const BODY_CLASS = [
  'prose prose-invert prose-sm sm:prose-base max-w-none text-gray-300',
  'prose-headings:font-display prose-headings:text-white',
  'prose-strong:text-white',
  'prose-a:text-accent-400 prose-a:no-underline hover:prose-a:text-accent-300',
  'marker:text-gray-600',
].join(' ');

interface ServiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRequestContact: () => void;
  title: string;
  longDescription: string;
  placement: string;
  bookingLabel: string;
  imageUrl?: string;
}

const ServiceModal: React.FC<ServiceModalProps> = ({ isOpen, onClose, onRequestContact, title, longDescription, placement, bookingLabel, imageUrl }) => {
  const modalContentRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    const handleClickOutside = (event: MouseEvent) => {
      if (modalContentRef.current && !modalContentRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Separate from the dismissal listeners: those depend on onClose, which the parent
  // re-creates every render, and re-running focus restoration would steal focus.
  useEffect(() => {
    if (!isOpen) return;

    const previouslyFocused = document.activeElement as HTMLElement | null;
    const previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    modalContentRef.current?.focus();

    const handleTabKey = (event: KeyboardEvent) => {
      if (event.key !== 'Tab' || !modalContentRef.current) return;

      const focusable = Array.from(
        modalContentRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
      ).filter(element => element.offsetParent !== null);
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (event.shiftKey && (active === first || active === modalContentRef.current)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleTabKey);

    return () => {
      document.removeEventListener('keydown', handleTabKey);
      document.body.style.overflow = previousBodyOverflow;
      previouslyFocused?.focus();
    };
  }, [isOpen]);

  // Links inside the authored copy are plain anchors, which would reload the app and
  // strand the reader on a page they arrived at through a full refresh.
  const handleBodyClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const anchor = (event.target as HTMLElement).closest('a');
    const href = anchor?.getAttribute('href');
    if (!href || !href.startsWith('/') || event.metaKey || event.ctrlKey || event.shiftKey) return;
    event.preventDefault();
    onClose();
    navigate(href);
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-gray-950/80 p-4">
      <div
        ref={modalContentRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="service-modal-title"
        tabIndex={-1}
        className="flex max-h-[calc(100vh-2rem)] w-full max-w-2xl flex-col rounded-lg border border-gray-700 bg-gray-900 shadow-xl focus:outline-none"
      >
        <div className="flex flex-shrink-0 items-start justify-between gap-4 border-b border-gray-800 p-6 md:px-8">
          <h2
            id="service-modal-title"
            className="font-display text-display-sm font-bold text-white sm:text-display-md"
          >
            {title}
          </h2>
          <button
            onClick={onClose}
            className="-mr-2 -mt-2 inline-flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-md text-gray-400 transition-colors duration-150 hover:text-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-400"
            aria-label="Close service details"
          >
            <X size={24} aria-hidden="true" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-6 md:px-8">
          {imageUrl && (
            <div className="mb-6 h-64 w-full overflow-hidden rounded-md">
              <img src={imageUrl} alt="" className="h-full w-full object-cover" />
            </div>
          )}
          {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
          <div
            className={BODY_CLASS}
            onClick={handleBodyClick}
            dangerouslySetInnerHTML={{ __html: longDescription }}
          />
        </div>

        {/* Peak intent gets the calendar, not a message box: the outlined action is
            the fallback for anyone who would rather write than book. */}
        <div className="flex flex-shrink-0 flex-col-reverse gap-3 border-t border-gray-800 p-6 sm:flex-row sm:justify-end md:px-8">
          <button
            onClick={onRequestContact}
            className="inline-flex min-h-[44px] items-center justify-center rounded-md border border-gray-600 px-5 py-2.5 text-sm font-medium text-gray-300 transition-colors duration-150 hover:border-gray-400 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-400 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-900"
          >
            Send a message
          </button>
          <BookAssessment placement={placement} variant="dialog" band="gray-900" label={bookingLabel} />
        </div>
      </div>
    </div>
  );
};

export default ServiceModal;
